/**
 * PlaybookDefaultsList — Resida's default behaviours: on/off switch and inline Reword per row,
 * plus the markers for a default Resida has since reworded and for one it retired.
 *
 * At property scope a default can also be turned back ON over an all-properties "off": that row
 * carries "On here · off elsewhere", and a property default can be reset to inherit. `idPrefix`
 * namespaces ids as in PlaybookRulesEditor.
 */

import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { PLAYBOOK_TEXT_MAX } from '@resida/api-types/agent';
import type { CatalogItem, PlaybookDefaultView } from '@resida/api-types/agent';

const SUB_LINE = 'text-sm text-muted-foreground';
const LINK_BUTTON = 'flex min-h-[44px] w-fit items-center text-sm text-primary hover:underline';

export interface PlaybookDefaultsListProps {
  idPrefix?: string;
  /**
   * The saved all-properties customizations when this list is a property group's; absent for the
   * all-properties list. A default turned off there is inherited here until the property says otherwise.
   */
  inheritedFrom?: PlaybookDefaultView[];
  /** Catalog items with tier `default`, in catalog order. */
  catalogDefaults: CatalogItem[];
  /** The draft's customizations (including retired ones). */
  customizations: PlaybookDefaultView[];
  /** Validation message per item id (from a 422). */
  errors: Record<string, string>;
  onToggle: (itemId: string) => void;
  onReword: (itemId: string, text: string) => void;
  onReset: (itemId: string) => void;
  onRemoveRetired: (itemId: string) => void;
}

export function PlaybookDefaultsList({
  idPrefix = 'playbook',
  inheritedFrom,
  catalogDefaults,
  customizations,
  errors,
  onToggle,
  onReword,
  onReset,
  onRemoveRetired,
}: PlaybookDefaultsListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const byId = new Map(customizations.map((c) => [c.itemId, c]));
  const inheritedOff = new Set((inheritedFrom ?? []).filter((d) => d.state === 'off').map((d) => d.itemId));
  const retired = customizations.filter((c) => c.retired);

  return (
    <section
      aria-labelledby={`${idPrefix}-defaults-heading`}
      className="flex flex-col gap-2"
      data-testid={`${idPrefix}-defaults-section`}
    >
      <div className="flex flex-col gap-0.5">
        <h3 id={`${idPrefix}-defaults-heading`} className="text-base font-semibold text-foreground">
          Defaults
        </h3>
        <span className="text-sm text-muted-foreground">
          Turn off or reword anything that doesn't match how you work.
        </span>
      </div>

      <ul className="flex flex-col">
        {catalogDefaults.map((item) => {
          const custom = byId.get(item.id);
          const isInheritedOff = !custom && inheritedOff.has(item.id);
          const isOff = custom?.state === 'off' || isInheritedOff;
          const isRewritten = custom?.state === 'rewritten';
          const isOnHere = custom?.state === 'on' && inheritedOff.has(item.id);
          const text = isRewritten ? (custom?.text ?? item.title) : item.title;
          const textId = `${idPrefix}-default-text-${item.id}`;
          const editing = editingId === item.id;
          const error = errors[item.id];
          return (
            <li
              key={item.id}
              data-testid={`${idPrefix}-default-${item.id}`}
              className={`flex flex-col gap-1 border-t border-border py-1.5 ${error ? 'rounded-md border border-destructive' : ''}`}
            >
              {editing ? (
                <div className="flex flex-col gap-2 py-2">
                  <label htmlFor={`${idPrefix}-default-input-${item.id}`} className="text-sm font-semibold text-foreground">
                    Reword this default
                  </label>
                  <Input
                    id={`${idPrefix}-default-input-${item.id}`}
                    data-testid={`${idPrefix}-default-input-${item.id}`}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="min-h-[44px]"
                  />
                  <span className={SUB_LINE}>Resida's: “{item.title}”</span>
                  <div className="flex items-center justify-between gap-2">
                    <span className={SUB_LINE}>
                      {editText.length} / {PLAYBOOK_TEXT_MAX}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                        data-testid={`${idPrefix}-default-cancel-${item.id}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="font-semibold"
                        onClick={() => {
                          onReword(item.id, editText);
                          setEditingId(null);
                        }}
                        data-testid={`${idPrefix}-default-done-${item.id}`}
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        id={textId}
                        className={`text-sm ${isOff ? 'text-muted-foreground line-through' : 'text-foreground'}`}
                      >
                        {text}
                      </span>
                      {isRewritten && custom?.reworded && (
                        <Badge variant="info" data-testid={`${idPrefix}-default-updated-${item.id}`}>
                          Resida updated this default
                        </Badge>
                      )}
                      {isOnHere && (
                        <Badge variant="secondary" data-testid={`${idPrefix}-default-on-here-${item.id}`}>
                          On here · off elsewhere
                        </Badge>
                      )}
                    </div>
                    {isRewritten && custom?.reworded && (
                      <div className={SUB_LINE}>
                        Your wording still applies. Resida's now reads: “{item.title}”
                        <button
                          type="button"
                          className={LINK_BUTTON}
                          onClick={() => onReset(item.id)}
                          data-testid={`${idPrefix}-default-reset-${item.id}`}
                        >
                          Use Resida's
                        </button>
                      </div>
                    )}
                    {isRewritten && !custom?.reworded && (
                      <div className={SUB_LINE}>
                        Your wording · Resida's: “{item.title}”
                        <button
                          type="button"
                          className={LINK_BUTTON}
                          onClick={() => onReset(item.id)}
                          data-testid={`${idPrefix}-default-reset-${item.id}`}
                        >
                          Reset
                        </button>
                      </div>
                    )}
                    {isOff && (
                      <span className={SUB_LINE}>
                        {isInheritedOff ? 'Off for all your properties.' : "Off — your agent won't do this."}
                      </span>
                    )}
                    {isOnHere && (
                      <button
                        type="button"
                        className={LINK_BUTTON}
                        onClick={() => onReset(item.id)}
                        data-testid={`${idPrefix}-default-reset-${item.id}`}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 shrink-0 text-muted-foreground"
                    aria-label={`Reword: ${item.title}`}
                    onClick={() => {
                      setEditingId(item.id);
                      setEditText(text);
                    }}
                    data-testid={`${idPrefix}-default-reword-${item.id}`}
                  >
                    <Pencil aria-hidden="true" />
                  </Button>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center">
                    <Switch
                      checked={!isOff}
                      onCheckedChange={() => onToggle(item.id)}
                      aria-labelledby={textId}
                      data-testid={`${idPrefix}-default-switch-${item.id}`}
                    />
                  </div>
                </div>
              )}
              {error && (
                <p role="alert" data-testid={`${idPrefix}-default-error-${item.id}`} className="px-1 text-sm text-destructive">
                  {error}
                </p>
              )}
            </li>
          );
        })}

        {retired.map((c) => (
          <li
            key={c.itemId}
            data-testid={`${idPrefix}-default-${c.itemId}`}
            className="flex flex-wrap items-center justify-between gap-3 border-t border-border py-1.5"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm text-muted-foreground line-through">{c.baseTitle}</span>
              <span className={SUB_LINE}>Retired by Resida — your change to it no longer has an effect.</span>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => onRemoveRetired(c.itemId)}
              data-testid={`${idPrefix}-default-remove-${c.itemId}`}
            >
              Remove
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default PlaybookDefaultsList;
